<template>
  <div v-if="!isRange" :class="$style.inputWrapper">
    <v-text-field
      outlined
      counter
      v-bind="$attrs"
      type="date"
      :min="min"
      :max="max"
      :label="label"
      hide-details
      @input="$emit('input', $event)"
      @blur="$emit('blur', $event)"
      @focus="$emit('focus', $event)"
    />

    <IconSelect
      icon="calendar"
      :class="$style.icon"
      :height="20"
      :width="18"
      :icon-props="{ 'stroke-width': 1 }"
    />
  </div>

  <div v-else :class="$style.inputRow">
    <div :class="$style.inputWrapper">
      <v-text-field
        v-bind="$attrs"
        :value="start"
        outlined
        counter
        type="date"
        :min="min"
        :max="maxStart"
        :label="label"
        :class="inputStyles(start)"
        hide-details
        @input="updateVal('start', $event)"
        @blur="updateVal('start', $event)"
        @focus="updateVal('start', $event)"
        @change="updateVal('start', $event)"
      />

      <IconSelect
        icon="calendar"
        :class="$style.icon"
        :height="20"
        :width="18"
        :icon-props="{ 'stroke-width': 1 }"
      />
    </div>

    <span :class="$style.minus">-</span>

    <div :class="$style.inputWrapper">
      <v-text-field
        v-bind="$attrs"
        :value="end"
        outlined
        counter
        type="date"
        :min="minEnd"
        :max="max"
        :label="labelEnd"
        :class="inputStyles(end)"
        hide-details
        @input="updateVal('end', $event)"
        @blur="updateVal('end', $event)"
        @focus="updateVal('end', $event)"
        @change="updateVal('end', $event)"
      />

      <IconSelect
        icon="calendar"
        :class="$style.icon"
        :height="20"
        :width="18"
        :icon-props="{ 'stroke-width': 1 }"
      />
    </div>
  </div>
</template>

<script>
import IconSelect from '@/js/components/Shared/IconSelect/IconSelect.vue'
import { formatISO } from 'date-fns'

// Either use as Basic Single Input with v-model, like:
// <BaseDateInput v-model="startdate" label="Start" min="2022-03-12" max="2022-03-24" />

// Or use as Range-Input, like:
// <BaseDateInput
//   :start-value="startdate"
//   :end-value="enddate"
//   :is-range="true"
//   label="Start"
//   label-end="End"
//   min="2022-03-12"
//   max="2022-03-24"
//   @input-start="startdate = $event"
//   @input-end="enddate = $event"
// />

export default {
  name: 'BaseDateInput',
  components: { IconSelect },
  props: {
    isRange: {
      type: Boolean,
      default: false,
    },
    startValue: {
      type: String,
      default: '',
    },
    endValue: {
      type: String,
      default: '',
    },
    min: {
      type: String,
      default: '',
    },
    max: {
      type: String,
      default: '',
    },
    label: {
      type: String,
      default: '',
    },
    labelEnd: {
      type: String,
      default: '',
    },
    autoSetEmptyDate: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      start: this.startValue,
      end: this.endValue,
    }
  },
  computed: {
    minEnd() {
      return this.start ? this.start : this.min
    },
    maxStart() {
      return this.end ? this.end : this.max
    },
  },
  watch: {
    start() {
      this.$emit('input-start', this.start)
    },
    end() {
      this.$emit('input-end', this.end)
    },
    startValue() {
      if (this.start !== this.startValue) {
        this.start = this.startValue
      }
    },
    endValue() {
      if (this.end !== this.endValue) {
        this.end = this.endValue
      }
    },
  },
  methods: {
    updateVal(fieldname, event) {
      const value = typeof event == 'string' ? event : event?.target?.value

      if (!value) {
        return
      }

      switch (fieldname) {
        case 'start':
          // Reset End if start is bigger
          if (new Date(value) > new Date(this.end)) {
            this.end = ''
          } else if (this.autoSetEmptyDate && this.end === '') {
            // Set Range End to (today/min date) if empty
            const endDate =
              new Date(value) > new Date()
                ? value
                : formatISO(new Date(), { representation: 'date' })

            this.end = endDate
          }
          this.start = value
          break
        case 'end':
          // Reset Start if end is smaller
          if (new Date(this.start) > new Date(value)) {
            this.start = ''
          } else if (this.autoSetEmptyDate && this.start === '') {
            // Set Range Start to Max Date if empty
            this.start = value
          }

          this.end = value
          break
      }
    },
    inputStyles(value) {
      return value ? [''] : [this.$style.emptyInput]
    },
  },
}
</script>
<style lang="scss" module>
.inputRow {
  @media screen and (max-width: map-get($grid-breakpoints, 'sm')) {
    flex-direction: column;
  }
  display: flex;
  flex-direction: row;
}

.inputWrapper {
  @media screen and (max-width: map-get($grid-breakpoints, 'sm')) {
    margin-bottom: 1.5rem;
  }
  position: relative;

  flex: 1;

  &:hover {
    svg {
      color: var(--blue) !important;
    }
  }
}

.minus {
  @media screen and (max-width: map-get($grid-breakpoints, 'sm')) {
    display: none;
  }
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  margin: 0 0.5rem;

  font-size: 1.5rem;
}

.icon {
  position: absolute;
  top: 50%;
  right: 16px;
  z-index: 10;

  background: white;
  transform: translateY(-50%);

  pointer-events: none;
}

.emptyInput {
  input {
    color: var(--grey-disabled) !important;
  }
}
</style>
